// Parity P6a: typed proposals (hybrid design §8.0, the universal Ask path). An agent action the
// policy answers with Ask (`propose`, services/api domain/agent-policy/decide.ts) is filed as an
// approval of one of these kinds; its payload is the approval's `proposal.change`, validated here
// when the approval is raised and when an approver edits it. Each payload binds a
// **precondition**: the subject's version, or the hash of the affected set computed before the
// approval was filed. The executor (registered by the package that owns the effect) refuses with
// `target_changed` when the target moved on, and the card shows the diff.
//
// Refusals are structured (`AgentRefusal`): a stable code from `ApprovalRefusalCode`, a sentence
// for people and agents, and the data a client needs (the diff of a stale target, the fence's end
// date). They travel in problem bodies (`refusal`) and in MCP results (`_meta.refusal`). A `deny`
// (fence, revoked access, hard prohibition) never creates an approval.
import { z } from "zod";
import { Approval } from "./agent-rows";
import { AgentActionClass, type ApprovalKind, FieldAudience, ViewKind } from "./enums";
import { BoundedJson, boundedRecord, LIMITS, Timestamp, text, Uuid } from "./primitives";
import { FieldOptions, ItemTemplatePayload, ViewFilter, ViewSort } from "./work-rows";

// Refusal codes ----------------------------------------------------------------------------------

export const ApprovalRefusalCode = z
  .enum([
    /** The subject's version or the affected set changed since the proposal was filed. */
    "target_changed",
    /** The board is fenced from agent reads ("Agents cannot read here", §8.3). */
    "fenced_read",
    /** The board refuses agent writes until a date ("Agents cannot write until …", §8.3). */
    "fenced_write_until",
    /** A hard prohibition (§8.1 `never` row) or a class set to Never: not approvable. */
    "never_class",
    /** The agent (or the person it acts for) no longer has access to the subject. */
    "access_revoked",
    /** The caller is not an approver of this approval. */
    "not_approver",
    /** The requester may not decide its own proposal (the worker never decides its own gate). */
    "self_decision",
    /** An approval kind with no payload schema or no executor yet. */
    "unknown_kind",
    /** The payload does not match its kind's schema. */
    "payload_invalid",
    /** The approval is no longer pending (decided or expired). */
    "not_pending",
    /**
     * CORE-PRECEDENTS: a person's standing ruling already decided this case (`data`: the
     * ruling's `precedent_id`, `decided_at`, option, approver); not filed again.
     */
    "precedent",
  ])
  .meta({ id: "ApprovalRefusalCode" });
export type ApprovalRefusalCode = z.infer<typeof ApprovalRefusalCode>;

export const AgentRefusal = z
  .object({
    code: ApprovalRefusalCode,
    message: z.string(),
    /** Code-specific data: `target_changed` the diff, `fenced_write_until` the date, … */
    data: z.record(z.string(), z.unknown()).optional(),
  })
  .meta({ id: "AgentRefusal" });
export type AgentRefusal = z.infer<typeof AgentRefusal>;

const REFUSAL_TEXT: Record<ApprovalRefusalCode, string> = {
  target_changed: "The target changed since this was proposed",
  fenced_read: "Agents cannot read this board",
  fenced_write_until: "Agents cannot write on this board yet",
  never_class: "Agents may never do this; it cannot be approved",
  access_revoked: "Access to this was revoked",
  not_approver: "Only an approver of this approval decides it",
  self_decision: "A proposal is not decided by the one who proposed it",
  unknown_kind: "This kind of proposal cannot be filed or applied yet",
  payload_invalid: "The proposal does not match its kind",
  not_pending: "This approval is no longer pending",
  precedent: "A standing ruling already decided this case",
};

/** A structured refusal: the code's sentence unless `message` says more. */
export function agentRefusal(
  code: ApprovalRefusalCode,
  data?: Record<string, unknown>,
  message?: string,
): AgentRefusal {
  return { code, message: message ?? REFUSAL_TEXT[code], ...(data ? { data } : {}) };
}

// Preconditions ----------------------------------------------------------------------------------

/** The subject as the proposer saw it: its `version` column. */
export const VersionPrecondition = z
  .strictObject({
    kind: z.literal("version"),
    // T1-BOARD-MCP: `template` (an `apply_template` proposal binds the template's version)
    subject_kind: z.enum([
      "task",
      "board",
      "view",
      "automation",
      "playbook_instance",
      "page",
      "template",
    ]),
    subject_id: Uuid,
    version: z.int().min(0),
  })
  .meta({ id: "VersionPrecondition" });

/**
 * The affected set computed before filing: sha256 of the sorted ids joined by `,` (`digest`,
 * so the event catalog never reads it as a secret-like `hash`), and its size.
 */
export const AffectedSetPrecondition = z
  .strictObject({
    kind: z.literal("affected_set"),
    digest: z.string().regex(/^[0-9a-f]{64}$/),
    count: z.int().min(0),
  })
  .meta({ id: "AffectedSetPrecondition" });

export const ProposalPrecondition = z
  .discriminatedUnion("kind", [VersionPrecondition, AffectedSetPrecondition])
  .meta({ id: "ProposalPrecondition" });
export type ProposalPrecondition = z.infer<typeof ProposalPrecondition>;

// Payloads per kind ------------------------------------------------------------------------------

const StatusKey = text(LIMITS.key);

/** `task_move`: a task to another board (Ask by default, §8.1). */
export const TaskMovePayload = z
  .strictObject({
    task_id: Uuid,
    from_board_id: Uuid,
    to_board_id: Uuid,
    /** The status on the destination board; omitted: matched by name, else its default. */
    to_status: StatusKey.optional(),
    position: z.number().finite().optional(),
    precondition: VersionPrecondition,
  })
  .meta({ id: "TaskMovePayload" });

/** `task_delete`: a task to the trash (never a purge). */
export const TaskDeletePayload = z
  .strictObject({
    task_id: Uuid,
    board_id: Uuid,
    precondition: VersionPrecondition,
  })
  .meta({ id: "TaskDeletePayload" });

const StatusDefInput = z.strictObject({
  key: StatusKey.optional(),
  name: text(LIMITS.name),
  category: z.enum(["todo", "active", "done"]),
});

/** `board_config`: the status set with its remap, or a field created, renamed or deleted. */
export const BoardConfigPayload = z
  .strictObject({
    board_id: Uuid,
    change: z.discriminatedUnion("op", [
      z.strictObject({
        op: z.literal("set_statuses"),
        statuses: z.array(StatusDefInput).min(1).max(LIMITS.list),
        /** Removed status key → the key its tasks move to. */
        remap: z.record(StatusKey, StatusKey).default({}),
      }),
      z.strictObject({
        op: z.literal("field_create"),
        name: text(LIMITS.name),
        type: text(LIMITS.key),
        options: BoundedJson.optional(),
        // T1-BOARD-MCP: who sees it, and whether a DE's value is proposed
        audience: FieldAudience.optional(),
        requires_approval: z.boolean().optional(),
      }),
      z.strictObject({ op: z.literal("field_rename"), field_id: Uuid, name: text(LIMITS.name) }),
      z.strictObject({ op: z.literal("field_delete"), field_id: Uuid }),
      // T1-BOARD-MCP: the structure a DE shapes a board with (MCP `create_field` and
      // `update_field` with every option, `create_view`, `set_default_view`, `propose`)
      z.strictObject({
        op: z.literal("field_update"),
        field_id: Uuid,
        name: text(LIMITS.name).optional(),
        options: FieldOptions.optional(),
        audience: FieldAudience.optional(),
        requires_approval: z.boolean().optional(),
      }),
      z.strictObject({
        op: z.literal("view_create"),
        kind: ViewKind,
        name: text(LIMITS.name),
        filter: ViewFilter.optional(),
        sort: ViewSort.optional(),
        group_by: z.string().max(64).nullable().optional(),
        visible_fields: z.array(z.string().max(64)).max(100).optional(),
        is_default: z.boolean().optional(),
      }),
      z.strictObject({ op: z.literal("view_default"), view_id: Uuid }),
      // B4-ITEM-TEMPLATES: a DE proposes a board's item template (MCP `create_item_template`,
      // `propose`); approved, it is saved as the approver
      z.strictObject({
        op: z.literal("item_template_create"),
        name: text(120),
        description: text(500).nullable().optional(),
        payload: ItemTemplatePayload,
      }),
      z.strictObject({
        op: z.literal("item_template_archive"),
        template_id: Uuid,
        /** The template's version when proposed: an edit since then refuses the archive. */
        template_version: z.int().min(1).optional(),
      }),
    ]),
    /** Tasks whose status or value the change rewrites (computed before filing). */
    affected_count: z.int().min(0),
    precondition: ProposalPrecondition,
  })
  .meta({ id: "BoardConfigPayload" });

/**
 * `automation`: an automation created, changed, toggled or deleted. Codex r1 #9: only a creation
 * has nothing to be stale against; a change, toggle or delete binds the automation's version.
 */
const existingAutomation = {
  board_id: Uuid.nullable(),
  automation_id: Uuid,
  precondition: VersionPrecondition,
};
export const AutomationPayload = z
  .discriminatedUnion("op", [
    z.strictObject({
      op: z.literal("create"),
      board_id: Uuid.nullable(),
      automation_id: z.null(),
      /** The automation's definition (trigger, conditions, actions). */
      spec: BoundedJson,
      enabled: z.boolean().optional(),
      precondition: z.null(),
    }),
    z.strictObject({ op: z.literal("update"), ...existingAutomation, spec: BoundedJson }),
    z.strictObject({ op: z.literal("toggle"), ...existingAutomation, enabled: z.boolean() }),
    z.strictObject({ op: z.literal("delete"), ...existingAutomation }),
  ])
  .meta({ id: "AutomationPayload" });

/**
 * T6-BOARD-NOTE `board_note`: a board's "How this board works" note as its owning DE rewrites it,
 * with the rule changes the note describes, decided as one (planned-outputs §6: the approval
 * shows both diffs). The precondition binds the note's version as the DE saw it (`subject_kind`
 * board, the board's id, `version` 0 while no note exists); each rule change binds its own.
 */
export const BoardNotePayload = z
  .strictObject({
    board_id: Uuid,
    /** The whole new note (the card diffs it against the note as it is; the change fits 16 KB). */
    body_markdown: z.string().max(8000),
    rules: z.array(AutomationPayload).max(20),
    /** How often the owner checks the board (minutes), when the change sets it. */
    wake_window_minutes: z.int().min(5).max(1440).optional(),
    /**
     * W1-FIXES: the check window the DE saw when it proposed a new one. Applied only while the
     * board still says that, so a person's later change of the window is never overwritten.
     */
    wake_window_from: z.int().min(1).max(1440).optional(),
    precondition: VersionPrecondition,
  })
  .meta({ id: "BoardNotePayload" });

/** `playbook_gate`: advance a playbook instance through a gate (decided by the gate's reviewer). */
export const PlaybookGatePayload = z
  .strictObject({
    instance_id: Uuid,
    stage_id: text(LIMITS.key),
    gate_id: text(LIMITS.key),
    /** The gate's reviewer seat, when the playbook names one (the approver, §8.0). */
    reviewer_seat_id: Uuid.nullable(),
    evidence: z.string().max(LIMITS.text).optional(),
    precondition: VersionPrecondition,
  })
  .meta({ id: "PlaybookGatePayload" });

/** `view_revision`: a board view's new revision against the revision it was based on. */
export const ViewRevisionPayload = z
  .strictObject({
    view_id: Uuid,
    board_id: Uuid,
    revision: BoundedJson,
    precondition: VersionPrecondition,
  })
  .meta({ id: "ViewRevisionPayload" });

/** `bulk`: one operation over an explicit affected set (> 10 items is Ask, §8.1). */
export const BulkPayload = z
  .strictObject({
    /** The class of the operation applied to each item. */
    class: AgentActionClass,
    /** The operation (`set_status`, `set_field`, `move`, …) and its arguments. */
    operation: text(LIMITS.key),
    args: BoundedJson,
    board_ids: z.array(Uuid).min(1).max(LIMITS.list),
    task_ids: z.array(Uuid).min(1).max(300),
    precondition: AffectedSetPrecondition,
  })
  .meta({ id: "BulkPayload" });

/** One line of an `apply_template` plan as the approval card shows it (`TemplatePlanItem`). */
export const TemplatePlanLine = z
  .strictObject({
    kind: text(LIMITS.key),
    name: text(LIMITS.name),
    action: z.enum(["create", "reuse", "link", "skip"]),
    parent: z.string().max(LIMITS.name).nullable().optional(),
    reason: z.string().max(LIMITS.text).nullable().optional(),
  })
  .meta({ id: "TemplatePlanLine" });

/**
 * T1-BOARD-MCP (universal task plan §b.3): `template_apply`, an agent's proposal to apply a
 * company template (`templates.apply` is a manager's). The server plans every row at filing
 * (`planTemplate`); the card shows that plan; approved, the decider's application plans again
 * against the same template version and applies it (`applyPlan`).
 */
export const TemplateApplyPayload = z
  .strictObject({
    template_id: Uuid,
    params: boundedRecord(z.string().max(LIMITS.name), 20).default({}),
    /** The plan as filed (counted and checked by the server; display only). */
    plan: z.array(TemplatePlanLine).max(500),
    precondition: VersionPrecondition,
  })
  .meta({ id: "TemplateApplyPayload" });

/**
 * B2-GATES (planned-outputs §4): one row of a batch review — the task as the DE filed it, and
 * the owner's decision on it. `keep` applies the move; `edit` applies the owner's edits (title,
 * date) and the move; `send_back` leaves the task where it is and posts the note to it (the DE
 * is woken with the decision); `drop` moves it to the batch's `drop_status` (when it names one)
 * with the reason as a note. Absent: keep.
 */
export const TaskBatchItem = z
  .strictObject({
    task_id: Uuid,
    /** The task's version as the DE saw it; a row changed since is skipped, never applied blind. */
    version: z.int().min(1),
    number: z.int().min(1),
    title: z.string().max(500),
    from_status: StatusKey,
    due_at: Timestamp.nullable(),
    decision: z.enum(["keep", "edit", "send_back", "drop"]).optional(),
    note: z.string().max(2000).optional(),
    edit: z
      .strictObject({
        title: text(500).optional(),
        due_at: Timestamp.nullable().optional(),
      })
      .optional(),
  })
  // a row sent back carries the note the DE is woken with
  .refine((i) => i.decision !== "send_back" || Boolean(i.note?.trim()), {
    message: "a row sent back needs a note",
    path: ["note"],
  })
  .meta({ id: "TaskBatchItem" });
export type TaskBatchItem = z.infer<typeof TaskBatchItem>;

/**
 * B2-GATES `task_batch`: one approval over N tasks of a board (the month of ideas), decided by
 * their owner row by row and applied as that person. The affected set binds the tasks filed.
 */
export const TaskBatchPayload = z
  .strictObject({
    board_id: Uuid,
    to_status: StatusKey,
    /** Where a dropped row goes (a status of the board); null: it stays, with the reason noted. */
    drop_status: StatusKey.nullable(),
    items: z.array(TaskBatchItem).min(1).max(100),
    precondition: AffectedSetPrecondition,
  })
  .meta({ id: "TaskBatchPayload" });

/** B2-GATES: one gate on a board transition (from any status when `from_status` is null). */
export const GateRule = z
  .strictObject({
    from_status: StatusKey.nullable(),
    to_status: StatusKey,
    mode: z.enum(["ask", "hold", "trust"]),
    /** How long a `hold` lets the owner pull the move back (minutes; default a day). */
    hold_minutes: z.int().min(5).max(10080).optional(),
  })
  .meta({ id: "GateRule" });
export type GateRule = z.infer<typeof GateRule>;

/**
 * B2-GATES `gate_change`: an agent's (the Chief of Staff's) change to a person's gates on a
 * board, read from what the person said (`sentence`). The person (the seat's holder) confirms
 * it; applied as that person. A DE never loosens a person's gate by itself.
 */
export const GateChangePayload = z
  .strictObject({
    board_id: Uuid,
    /** The person's seat whose gates these are. */
    seat_id: Uuid,
    gates: z.array(GateRule).min(1).max(20),
    sentence: z.string().max(1000).optional(),
    precondition: z.null(),
  })
  .meta({ id: "GateChangePayload" });

/**
 * BIZ-PLAYBOOKS `doc_update`: a DE's new version of a doc (a playbook is an SOP page), the whole
 * body as markdown, against the page version it read. Decided by the playbook's owner seat
 * (`owner_seat_id`, checked against the page's owner at filing), then the chain; applied as the
 * deciding person, only while the page is still at that version.
 */
export const DocUpdatePayload = z
  .strictObject({
    page_id: Uuid,
    title: z.string().min(1).max(500).optional(),
    body_markdown: z.string().max(60_000),
    /** Why the playbook should change (what the DE saw in its runs). */
    reason: z.string().max(2000).optional(),
    /** The playbook's owner seat when it has one (the approver), else null. */
    owner_seat_id: Uuid.nullable(),
    precondition: VersionPrecondition,
  })
  .meta({ id: "DocUpdatePayload" });

/**
 * BIZ-CLIENT-HEALTH `client_survey`: a DE's NPS/CSAT survey to a client contact, held when its
 * `external` class asks. Decided by the client's account owner seat (`account_seat_id`, checked
 * at filing), then the chain; sent as the deciding person.
 */
export const ClientSurveyPayload = z
  .strictObject({
    space_id: Uuid,
    kind: z.enum(["nps", "csat"]),
    recipient_name: z.string().trim().min(1).max(200),
    recipient_email: z.email().max(320),
    account_seat_id: Uuid.nullable(),
    precondition: z.null(),
  })
  .meta({ id: "ClientSurveyPayload" });

/** The typed kinds and their payload schemas (the approval's `proposal.change`). */
export const typedProposalPayloads = {
  task_move: TaskMovePayload,
  task_delete: TaskDeletePayload,
  board_config: BoardConfigPayload,
  template_apply: TemplateApplyPayload,
  automation: AutomationPayload,
  playbook_gate: PlaybookGatePayload,
  view_revision: ViewRevisionPayload,
  bulk: BulkPayload,
  board_note: BoardNotePayload,
  task_batch: TaskBatchPayload,
  gate_change: GateChangePayload,
  doc_update: DocUpdatePayload,
  client_survey: ClientSurveyPayload,
} as const satisfies Partial<Record<z.infer<typeof ApprovalKind>, z.ZodType>>;

export type TypedApprovalKind = keyof typeof typedProposalPayloads;
export const TYPED_APPROVAL_KINDS = Object.keys(typedProposalPayloads) as TypedApprovalKind[];

export function isTypedApprovalKind(kind: string): kind is TypedApprovalKind {
  return Object.hasOwn(typedProposalPayloads, kind);
}

export type TypedPayload<K extends TypedApprovalKind> = z.infer<(typeof typedProposalPayloads)[K]>;

/**
 * Validates a typed kind's payload. Kinds that predate §8.0 (task_change, message, …) are not
 * typed here and pass through (`ok` with the change as given).
 */
export function parseTypedPayload(
  kind: string,
  change: unknown,
): { ok: true; payload: unknown } | { ok: false; refusal: AgentRefusal } {
  if (!isTypedApprovalKind(kind)) return { ok: true, payload: change };
  const r = typedProposalPayloads[kind].safeParse(change);
  if (r.success) return { ok: true, payload: r.data };
  return {
    ok: false,
    refusal: agentRefusal("payload_invalid", {
      kind,
      issues: r.error.issues.slice(0, 10).map((i) => ({
        path: i.path.join("."),
        message: i.message,
      })),
    }),
  };
}

/** The precondition a typed payload binds (null for kinds without one). */
export function preconditionOf(kind: string, change: unknown): ProposalPrecondition | null {
  const p = parseTypedPayload(kind, change);
  if (!p.ok || !isTypedApprovalKind(kind)) return null;
  return (p.payload as { precondition?: ProposalPrecondition | null }).precondition ?? null;
}

// Approval reads ---------------------------------------------------------------------------------

/**
 * An approval as `GET approvals` / `GET approvals/{id}` serve it: the row plus what the caller may
 * do with it (§3.1 cards). `can_view`: the content is the caller's to read; `can_decide`: the
 * caller is a decider and the approval is still pending and not expired.
 */
export const ApprovalRead = Approval.extend({
  can_view: z.boolean(),
  can_decide: z.boolean(),
}).meta({ id: "ApprovalRead" });
export type ApprovalRead = z.infer<typeof ApprovalRead>;

/** `target_changed` data: what the proposal saw against what is there now. */
export const TargetChangedDiff = z
  .object({
    expected: ProposalPrecondition,
    current: z.union([
      z.object({ version: z.int().min(0).nullable() }),
      z.object({
        digest: z.string(),
        count: z.int().min(0),
        added: z.array(Uuid),
        removed: z.array(Uuid),
      }),
    ]),
    checked_at: Timestamp,
  })
  .meta({ id: "TargetChangedDiff" });
export type TargetChangedDiff = z.infer<typeof TargetChangedDiff>;
